import { type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import type { Api } from "../../../services/api";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import { searchLabels } from "../../../domains/app/labelSearch";
import {
  pickerProfessions,
  type ProfessionListState,
} from "../components/professionPriority";

type ProfessionItem = Pick<
  Api["get"]["/webapp/public/professions"]["output"]["body"][number],
  "uid" | "translations" | "onboardingVisibility"
>;

/**
 * The short list first, the rest behind "Show more". A search looks through
 * both lists. A selected profession from the longer list stays listed while
 * the rest is hidden.
 */
export function ProfessionStep({
  professions,
  selected,
  onSelect,
  onNext,
  searchQuery,
  professionList,
  onShowMore,
}: {
  professions: ProfessionItem[];
  selected: string | null;
  onSelect: (uid: string) => void;
  onNext: () => void;
  searchQuery: string;
  professionList: ProfessionListState;
  onShowMore: () => void;
}) {
  const { t } = useTranslation();
  const selectedRowClass =
    "border-ds-border-selected bg-ds-secondary text-ds-primary";

  const { listed, all, offersMore } = pickerProfessions({
    professions,
    selected,
    list: professionList,
  });
  const searching = searchQuery.trim().length > 0;

  const filteredProfessions = searchLabels({
    items: searching ? all : listed,
    query: searchQuery,
    labelOf: (profession) => getItemTranslation(profession),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (selected !== null) onNext();
  };

  return (
    <form
      id="onboarding-form-profession"
      className="flex w-full flex-col"
      onSubmit={handleSubmit}
    >
      <div className="flex w-full flex-col gap-2">
        {filteredProfessions.length === 0 ? (
          <p className="pt-8 text-center font-dm-sans text-ui-body text-ds-slate-500">
            {t("No profession found")}
          </p>
        ) : (
          filteredProfessions.map((profession) => (
            <button
              key={profession.uid}
              type="button"
              className={cn(
                "flex h-14 w-full shrink-0 items-center rounded-2xl border px-4 text-left transition-colors",
                selected === profession.uid
                  ? selectedRowClass
                  : "border-ds-black/6 bg-white"
              )}
              onClick={() => {
                onSelect(profession.uid);
              }}
            >
              <span className="font-dm-sans text-ui-body font-medium text-primary-black">
                {getItemTranslation(profession)}
              </span>
            </button>
          ))
        )}
        {searching || !offersMore ? null : (
          <button
            type="button"
            className="flex h-14 w-full shrink-0 items-center justify-center font-dm-sans text-ui-body font-medium text-ds-primary"
            onClick={onShowMore}
          >
            {t("Show more professions")}
          </button>
        )}
      </div>
    </form>
  );
}
